/**
 * 统一推理：拍一帧 → L1 检测狗 → 若有狗则 L2 BCS + L3 品种(模型) + L3 情绪(多帧姿态)
 */

import type { AnalysisResult } from '../types';
import { detectDog, initObjectDetector } from './level1';
import { computeBCS, initPoseLandmarker } from './level2';
import {
  initBreedModel,
  predictBreed,
  inferEmotionFromPoseHistory,
} from './level3';
import type { PoseLandmarksFrame } from './level2';

let inited = false;

/** 只等 L1/L2 就绪即放行，品种模型后台加载不阻塞（手机网络不稳时避免卡在加载） */
export async function ensureModelsLoaded(): Promise<void> {
  if (inited) return;
  await Promise.all([
    initObjectDetector(),
    initPoseLandmarker(),
  ]);
  inited = true;
  initBreedModel().catch(() => {}); // 品种后台加载，失败也不影响使用
}

export type RunAnalysisOut =
  | { result: AnalysisResult }
  | { result: null; noDogHint?: 'person' };

/**
 * 从 video 当前帧截图为 canvas，跑 L1/L2/L3；品种用 L1 bbox 裁剪；情绪用多帧姿态历史
 * 无狗时返回 { result: null, noDogHint? } 供前端区分提示（如检测到人）
 */
export async function runAnalysis(
  video: HTMLVideoElement,
  poseHistory: PoseLandmarksFrame[] | null
): Promise<RunAnalysisOut> {
  if (video.readyState < 2) return { result: null };
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { result: null };
  ctx.drawImage(video, 0, 0);

  const l1 = detectDog(canvas);
  if (!l1 || !l1.hasDog) {
    return {
      result: null,
      noDogHint: l1?.detectedPerson ? 'person' : undefined,
    };
  }

  const l2 = computeBCS(canvas);
  const l3Breed = await predictBreed(canvas, l1.bbox);
  const l3Emotion = inferEmotionFromPoseHistory(poseHistory);

  return {
    result: {
      breed: l3Breed.breed,
      breedMeta: l3Breed.breedMeta,
      emotion: l3Emotion.emotion,
      emotionMeta: l3Emotion.emotionMeta,
      health: l2.health,
      healthMeta: l2.healthMeta,
    },
  };
}
